import React, {useState, useEffect, useRef} from 'react';
import ExperienceFilter from '@Components/ExperienceFilter';
import RangeFilter from '@Components/RangeFilter';
import SkillsFilter from '@Components/SkillsFilter';
import {Link, useHistory} from 'react-router-dom';
import {injectIntl} from 'react-intl';
import filtersToQueryString from 'filtersToQueryString';
import Icon from '@Components/Icon';
import dispatchRerender from 'dispatchRerender';
import scrollTo from 'scrollTo';

const HeaderForFilters = ({intl, skills, searchParams}) => {
  const filters = useRef(null);
  const [state, setState] = useState(searchParams);
  const history = useHistory();
  const [badges, setBadges] = useState({
    range: 0,
    experience: 0,
    skills: 0,
    total: 0,
  });

  /**
   * Calculates badges for filters based on current selected filters.
   *
   * @return {{
   *   total: number,
   *   range: number,
   *   experience: number,
   *   skills: number,
   * }}
   */
  const calcBadges = () => {
    const badges = {
      range: [state.radius].filter((v) => v).length ? 1 : 0,
      experience: [state.experience].filter((v) => v).length ? 1 : 0,
      skills: 0,
      total: 0,
    };

    if (state.skills) {
      badges.skills = Array.isArray(state.skills) ?
        state.skills.length :
        state.skills.split(',').length;
    }

    // Calc all badges.
    badges.total = badges.range + badges.experience + badges.skills;
    return badges;
  };

  const changeSearchPath = (params) => {
    if (params.skills && (typeof params.skills === 'object' || params.skills instanceof String)) {
      const data = Object.entries(params.skills).filter(([key, value]) => value === true);
      params.skills = Object.keys(Object.fromEntries(data)).join(',');
    }
    history.push({
      'pathname': '/find-employees/search-result/',
      'search': filtersToQueryString(params),
    });
  };

  const clearFilters = (event) => {
    event.preventDefault();
    switch (event.target.name) {
      case 'range':
        setState((prev) => {
          return {
            ...prev,
            radius: null,
          };
        });
        delete searchParams.radius;
        changeSearchPath(searchParams);
        break;
      case 'experience':
        setState((prev) => {
          return {
            ...prev,
            experience: null,
          };
        });
        delete searchParams.experience;
        changeSearchPath(searchParams);
        break;
      case 'skills':
        setState((prev) => {
          return {
            ...prev,
            skills: null,
          };
        });
        delete searchParams.skills;
        changeSearchPath(searchParams);
        break;
      case 'all':
        setState((prev) => {
          return {
            ...prev,
            radius: null,
            experience: null,
            skills: null,
          };
        });

        // eslint-disable-next-line no-unused-vars
        const {radius, experience, skills, page, ...rest} = searchParams;
        changeSearchPath(rest);
        scrollTo('#root');
        break;
    }
  };

  const applyFilters = (event) => {
    event.preventDefault();
    const stickyGhostBox = document.querySelector('.sticky-ghost-box');
    if (filters.current.classList.contains('drop-down') && stickyGhostBox) {
      stickyGhostBox.scrollIntoView({behavior: 'smooth'});
    }
    setTimeout(() => {
      changeSearchPath({...state});
    }, 100);
  };

  useEffect(() => {
    // Re-attach orbit events.
    dispatchRerender();
    // Re-calculate filter badges.
    setBadges(calcBadges());

    // Init sticky.
    const event = document.createEvent('Event');
    event.initEvent('init-sticky', true, true);
    document.dispatchEvent(event);

    // Remove sticky wrapper to prevent duplicates on each search.
    return () => {
      const stickyBox = document.getElementsByClassName('sticky-ghost-box');
      if (stickyBox.length) {
        for (const item of stickyBox) {
          item.parentElement.removeChild(item);
        }
      }
    };
  }, []);

  const renderMobileFilters = () => {
    return (
      <div className="modal" data-rs-modal="mobile-filters">
        <div className="modal__dialog bg-variant-brand-tertiary" role="dialog" aria-modal={true} aria-labelledby="#" data-rs-modal-dialog="data-rs-modal-dialog">
          <div className="modal__header" data-rs-modal-header={true}>
            <div className="modal__title">
              { intl.formatMessage({id: 'Filters.HeaderForFilters.FilterLabel'}) }
            </div>
            <Link to="" className={`modal__label modal__label--clear ${badges.total ? '' : 'hidden'}`} name="all" onClick={clearFilters}>
              { intl.formatMessage({id: 'Filters.ClearAllFilters'}) }
            </Link>
            <button className="button--icon-only modal__close" data-rs-modal-close-trigger={true}>
              <span className="icon icon--inline hidden--from-l">
                <Icon type="close"/>
              </span>
              <span className="icon icon-l icon--inline hidden--until-l">
                <Icon type="close-30"/>
              </span>
            </button>
          </div>
          <div className="modal__main" data-rs-modal-main={true}>
            <div
              className="tab-navigation tab-navigation-filter tab-navigation-filter--icons tab-navigation-filter--animate"
              data-rs-toggable-group="toggable-group__item--active"
            >
              <ul className="toggable-group">
                <li className="toggable-group__item toggable-group__item--active" data-rs-toggable-group-item="active" data-rs-toggable-target="#mobile-range">
                  <span className={`badge bg-brand--blue badge--s ${badges.range ? '' : 'hidden'}`}>
                    {badges.range}
                  </span>
                  <span className="icon">
                    <Icon type="marker"/>
                  </span>
                  <span className="label">
                    { intl.formatMessage({id: 'Filters.RangePopupTitle'}) }
                  </span>
                </li>
                <li className="toggable-group__item" data-rs-toggable-group-item={true} data-rs-toggable-target="#mobile-experience">
                  <span className={`badge bg-brand--blue badge--s ${badges.experience ? '' : 'hidden'}`}>
                    {badges.experience}
                  </span>
                  <span className="icon">
                    <Icon type="dashboard"/>
                  </span>
                  <span className="label">
                    { intl.formatMessage({id: 'Filters.ExperiencePopupTitle'}) }
                  </span>
                </li>
                <li className="toggable-group__item" data-rs-toggable-group-item={true} data-rs-toggable-target="#mobile-skill-set">
                  <span className={`badge bg-brand--blue badge--s ${badges.skills ? '' : 'hidden'}`}>
                    {badges.skills}
                  </span>
                  <span className="icon">
                    <Icon type="promotion"/>
                  </span>
                  <span className="label">
                    { intl.formatMessage({id: 'Filters.SkillsPopupTitle'}) }
                  </span>
                </li>
              </ul>
            </div>
            <div className="tab-content tab-content--animate" id="mobile-range">
              <div className="form-group">
                <RangeFilter range={state?.radius} setRange={setState} />
              </div>
            </div>
            <div className="tab-content tab-content--animate" id="mobile-experience" hidden={true}>
              <ExperienceFilter experience={state?.experience} setExperience={setState} />
            </div>
            <div className="tab-content tab-content--animate" id="mobile-skill-set" hidden={true}>
              <div className="form-group">
                <SkillsFilter skills={skills} dataSkills={state?.skills} setDataSkills={setState} />
              </div>
            </div>
          </div>
          <div className="modal__footer divider divider--top" data-rs-modal-footer={true}>
            <div className="button button--m button--filled button--full-width show-jobs" onClick={applyFilters}>
              { intl.formatMessage({id: 'Filters.HeaderForFilters.ShowResultsButton'})}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderDesktopFilters = () => {
    return (
      <div ref={filters} className="bg-brand--off-white" data-rs-sticky={true} data-rs-sticky-bg="off-white">
        <div className="wrapper">
          <div className="filter-bar">
            <div className="filter-bar__toggle hidden--from-l">
              <div className="filter-bar__toggle__trigger" data-rs-modal-trigger="mobile-filters">
                <span className="icon icon--inline">
                  <Icon type="filter"/>
                </span>
                <span>{ intl.formatMessage({id: 'Filters.HeaderForFilters.FilterLabel'})}</span>
                <span className={`badge bg-brand--blue badge--s ${badges.total ? '' : 'hidden'}`}>
                  { badges.total }
                </span>
              </div>
            </div>
            <ul className="filter-bar__menu hidden--until-l">
              <li className="filter-bar__menu-item">
                <div className="filter-bar__menu-item__trigger" data-rs-popover-trigger="range">
                  { intl.formatMessage({id: 'Filters.RangePopupTitle'}) }
                  <span className="icon icon--xs fill--dark-blue">
                    <Icon type="chevron-down-8"/>
                  </span>
                  <span className={`badge bg-brand--blue badge--s ${badges.range ? '' : 'hidden'}`}>
                    { badges.range }
                  </span>
                </div>
                <div className="filter-bar__popover-wrapper">
                  <div className="popover popover--arrow-left bg-brand--off-white hidden--until-l" data-rs-popover="range">
                    <div className="popover__dialog">
                      <div className="popover__title">
                        {intl.formatMessage({id: 'Filters.RangePopupTitle'})}
                      </div>
                      <div className="form-group">
                        <RangeFilter range={state?.radius} setRange={setState} />
                      </div>
                      <div className="popover__action">
                        <div className={`button button--m button--filled show-jobs`} onClick={applyFilters}>
                          {intl.formatMessage({id: 'Filters.ShowResults.Label'})}
                        </div>
                        <Link to="" name="range" onClick={clearFilters}>
                          {intl.formatMessage({id: 'Filters.ClearFilter'})}
                        </Link>
                      </div>
                    </div>
                  </div>
                  <div className="modal__overlay modal__overlay--light hidden--until-l" data-rs-popover-overlay={true} />
                </div>
              </li>
              <li className="filter-bar__menu-item">
                <div className="filter-bar__menu-item__trigger" data-rs-popover-trigger="experience">
                  { intl.formatMessage({id: 'Filters.ExperiencePopupTitle'}) }
                  <span className="icon icon--xs fill--dark-blue">
                    <Icon type="chevron-down-8"/>
                  </span>
                  <span className={`badge bg-brand--blue badge--s ${badges.experience ? '' : 'hidden'}`}>
                    { badges.experience }
                  </span>
                </div>
                <div className="filter-bar__popover-wrapper">
                  <div className="popover popover--arrow-left bg-brand--off-white hidden--until-l" data-rs-popover="experience">
                    <div className="popover__dialog">
                      <div className="popover__title">
                        {intl.formatMessage({id: 'Filters.ExperiencePopupTitle'})}
                      </div>
                      <div className="form-group">
                        <ExperienceFilter experience={state?.experience} setExperience={setState} />
                      </div>
                      <div className="popover__action">
                        <div className={`button button--m button--filled show-jobs`} onClick={applyFilters}>
                          {intl.formatMessage({id: 'Filters.ShowResults.Label'})}
                        </div>
                        <Link to="" name="experience" onClick={clearFilters}>
                          {intl.formatMessage({id: 'Filters.ClearFilter'})}
                        </Link>
                      </div>
                    </div>
                  </div>
                  <div className="modal__overlay modal__overlay--light hidden--until-l" data-rs-popover-overlay={true} />
                </div>
              </li>
              <li className="filter-bar__menu-item">
                <div className="filter-bar__menu-item__trigger" data-rs-popover-trigger="skills">
                  { intl.formatMessage({id: 'Filters.SkillsTitle'}) }
                  <span className="icon icon--xs fill--dark-blue">
                    <Icon type="chevron-down-8"/>
                  </span>
                  <span className={`badge bg-brand--blue badge--s ${badges.skills ? '' : 'hidden'}`}>
                    { badges.skills }
                  </span>
                </div>
                <div className="filter-bar__popover-wrapper">
                  <div className="popover popover--wide popover--arrow-left bg-brand--off-white hidden--until-l" data-rs-popover="skills">
                    <div className="popover__dialog">
                      <div className="popover__dialog-inner">
                        <div className="popover__title divider pb-s">
                          {intl.formatMessage({id: 'Filters.SkillsPopupTitle'})}
                        </div>
                        <div className="form-group form-group--two-cols popover__content pt-s">
                          <SkillsFilter skills={skills} dataSkills={state?.skills} setDataSkills={setState} />
                        </div>
                        <div className="popover__action divider divider--top">
                          <div className={`button button--m button--filled show-jobs`} onClick={applyFilters}>
                            {intl.formatMessage({id: 'Filters.ShowResults.Label'})}
                          </div>
                          <Link to="" name="skills" onClick={clearFilters}>
                            {intl.formatMessage({id: 'Filters.ClearFilter'})}
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="modal__overlay modal__overlay--light hidden--until-l" data-rs-popover-overlay={true} />
                </div>
              </li>
            </ul>
            <Link to="" className={`filter-bar__label hidden--until-l ${badges.total ? '' : 'hidden'}`} name="all" onClick={clearFilters}>
              { intl.formatMessage({id: 'Filters.ClearAllFilters'}) }
            </Link>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {renderDesktopFilters()}
      {renderMobileFilters()}
    </>
  );
};

export default injectIntl(HeaderForFilters);
